import { useState, useEffect } from 'react';
import { X, CircleNotch, Sparkle, PaperPlaneRight, PencilSimple, CheckCircle } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { API } from './constants';

export function BroadcastEditor({ campaign, onClose, onSent }) {
  const [brief, setBrief] = useState(campaign.broadcast_brief || '');
  const [draft, setDraft] = useState(campaign.broadcast_draft || null);
  const [audience, setAudience] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentResult, setSentResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/email/segments/count`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(campaign.segment_filter || {}),
    }).then(r => r.json()).then(d => setAudience(d.count)).catch(() => {});
  }, [campaign.segment_filter]);

  useEffect(() => {
    if (draft) {
      setEditSubject(draft.subject || '');
      setEditBody(draft.body_text || '');
    }
  }, [draft]);

  const handleGenerate = async () => {
    setError(''); setGenerating(true);
    try {
      const res = await fetch(`${API}/email/campaigns/${campaign.campaign_id}/broadcast/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generatie mislukt');
      setDraft(data.draft);
      if (data.audience_count != null) setAudience(data.audience_count);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    }
    setGenerating(false);
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true); setError('');
    try {
      const res = await fetch(`${API}/email/campaigns/${campaign.campaign_id}/broadcast/draft`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: editSubject, body_text: editBody }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Opslaan mislukt');
      setDraft(data.draft);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    }
    setSavingDraft(false);
  };

  const handleSend = async () => {
    if (!window.confirm(`Weet je zeker dat je deze broadcast naar ${audience ?? '?'} contact(en) wilt verzenden?`)) return;
    setSending(true); setError('');
    try {
      const res = await fetch(`${API}/email/campaigns/${campaign.campaign_id}/broadcast/send`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verzenden mislukt');
      setSentResult(data);
    } catch (e) {
      setError(e.message);
    }
    setSending(false);
  };

  const finishedSent = !!sentResult;
  const hasDraft = !!draft;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="broadcast-editor">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2 min-w-0">
            <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-purple-100 text-purple-700 border border-purple-200 shrink-0">Broadcast</Badge>
            <h3 className="font-heading text-base font-semibold truncate">{campaign.name}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid="broadcast-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Audience */}
          <div className="flex items-center justify-between p-3 bg-purple-50/40 border border-purple-200 rounded-sm" data-testid="broadcast-audience">
            <div className="text-xs text-zinc-600">Doelgroep ({Object.entries(campaign.segment_filter || {}).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(',') : v}`).join(' | ') || 'iedereen'})</div>
            <div className="text-xs"><span className="font-data text-base font-bold text-purple-700">{audience === null ? '...' : audience}</span> contacten</div>
          </div>

          {/* Brief */}
          <div>
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Wat moet er in de mail staan?</label>
            <textarea
              data-testid="broadcast-brief"
              value={brief} onChange={e => setBrief(e.target.value)}
              placeholder="Bijv: Aankondiging van onze nieuwe roestvrij-vrije wanddoorvoer PoolFIX-WD60. Voordelen: chloorbestendig, 2x lichter dan RVS, levertijd 5 werkdagen. Roep klanten op om voor 30 mei te bestellen voor 10% lanceer­korting."
              rows={5}
              className="w-full border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
              disabled={finishedSent}
            />
            <button onClick={handleGenerate} disabled={generating || !brief.trim() || finishedSent} data-testid="broadcast-generate-btn"
              className="mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {generating ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} />}
              {hasDraft ? 'Opnieuw genereren met AI' : 'Genereer email met AI'}
            </button>
          </div>

          {/* Draft */}
          {hasDraft && !finishedSent && (
            <div className="border-2 border-purple-200 bg-purple-50/30 p-4 rounded-sm" data-testid="broadcast-draft">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-purple-700 uppercase tracking-wider">AI Conceptemail</span>
                <button onClick={() => setEditing(!editing)} data-testid="broadcast-edit-toggle"
                  className="text-xs text-[#0055FF] hover:underline flex items-center gap-1">
                  <PencilSimple size={12} /> {editing ? 'Annuleren' : 'Bewerken'}
                </button>
              </div>

              {editing ? (
                <div className="space-y-2">
                  <Input value={editSubject} onChange={e => setEditSubject(e.target.value)} placeholder="Onderwerp" className="rounded-sm text-sm" data-testid="broadcast-edit-subject" />
                  <textarea value={editBody} onChange={e => setEditBody(e.target.value)}
                    rows={10}
                    className="w-full border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
                    data-testid="broadcast-edit-body" />
                  <button onClick={handleSaveDraft} disabled={savingDraft} data-testid="broadcast-save-edit"
                    className="px-3 py-1.5 text-xs font-medium bg-[#0055FF] text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {savingDraft ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-zinc-800">{draft.subject}</div>
                  <div className="text-sm text-zinc-700 whitespace-pre-wrap bg-white p-3 border border-zinc-200 rounded-sm leading-relaxed">{draft.body_text}</div>
                  <p className="text-[10px] text-zinc-400">[voornaam] wordt per ontvanger automatisch ingevuld.</p>
                </div>
              )}
            </div>
          )}

          {/* Sent result */}
          {finishedSent && (
            <div className="border-2 border-emerald-200 bg-emerald-50 p-5 rounded-sm flex items-center gap-3" data-testid="broadcast-sent-result">
              <CheckCircle size={32} weight="bold" className="text-emerald-600 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-emerald-800">Broadcast verzonden!</div>
                <div className="text-xs text-emerald-700 mt-0.5">
                  {sentResult.sent} van {sentResult.total} ontvangers succesvol
                  {sentResult.failed > 0 && <span> · {sentResult.failed} mislukt</span>}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="text-xs px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-sm" data-testid="broadcast-error">{error}</div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-200 bg-zinc-50">
          {finishedSent ? (
            <button onClick={() => { onSent && onSent(); onClose(); }} className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white hover:bg-blue-700 transition-colors">Sluiten</button>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 transition-colors">Annuleren</button>
              <button onClick={handleSend} disabled={!hasDraft || sending || editing} data-testid="broadcast-send-btn"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                {sending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={14} />}
                Goedkeuren & Verzenden
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
